import { t } from "i18next";
import moment from "moment";

import { RESOLUTION_TYPES } from "./offerPlan.constants";

import type { GeoblockMode } from "../../api/geoblock.types";

export interface OfferPlanPriceFields {
  currency?: string | null;
  price?: number | null;
}

export interface OfferPlanRentalDurationFields {
  limit_date?: string | number | null;
  rental_duration?: number;
}

export interface OfferPlanGeoblockFields {
  geoblock_mode?: GeoblockMode;
  country_groups?: { label?: string }[];
}

export interface OfferPlanStatusFields {
  enabled: boolean;
}

/** "" when no price is set: the table draws its own dash in an empty cell. */
export function getPlanDisplayedPrice(plan: OfferPlanPriceFields): string {
  const { currency, price } = plan;

  return price !== undefined && price !== null
    ? `${price % 1 !== 0 ? Number(price).toFixed(2) : price} ${currency}`
    : "";
}

/**
 * Displayed rental duration / access limit for a plan: a formatted date when
 * a `limit_date` is set, otherwise a trial period duration or "unlimited".
 * Ported from the static `OfferPlan.getDisplayedRentalDuration`.
 */
export function getPlanDisplayedRentalDuration(
  plan: OfferPlanRentalDurationFields,
): string {
  const { limit_date: limitDate, rental_duration: rentalDuration } = plan;

  if (limitDate) {
    return limitDate !== 0
      ? moment(limitDate).format("DD/MM/YYYY")
      : t("editor-offers:unlimited");
  }

  return !rentalDuration || rentalDuration < 0
    ? t("editor-offers:unlimited")
    : t("editor-offers:trial_period_value", { count: rentalDuration });
}

/** Geo restriction label for a plan, e.g. "Disponible en Europe, France". */
export function getPlanGeoblockLabel(
  plan: OfferPlanGeoblockFields,
  countryLabelList: string[],
): string {
  const labelList = [
    ...(plan.country_groups?.map(({ label }) => label) ?? []),
    ...countryLabelList,
  ];

  return `${t(`common:${plan.geoblock_mode}_countries`)} ${labelList.join(", ")}`;
}

/**
 * Enabled/disabled status label for a plan.
 * Ported from the `status` field derived in the `OfferPlan` constructor.
 */
export function getPlanStatus(plan: OfferPlanStatusFields): string {
  return plan.enabled ? "enabled" : "disabled";
}

/** Resolution options offered for a plan. Ported from `OfferPlan.resolutions`. */
export function getPlanResolutionOptions(): { label: string; value: string }[] {
  const { SD, HD, UHD } = RESOLUTION_TYPES;

  return [SD, HD, UHD].map((value) => ({
    value,
    label: value.toUpperCase(),
  }));
}
